import querystring from 'querystring';
import {
  BuildDataAndOpenWebViewPaymentModalArgs,
  GetMappedUrlForBundleArgs,
  GetMappedUrlForServiceArgs,
  GetPaidUntilDateArgs,
  GetPromotionsDataArgs,
  GetPromotionsDataType,
  GetSubtitleArgs,
  GetUrlArgs,
} from './PromotionFlow.helpers.types';
import { getLessPrice } from './Promotions.helpers';
import { PromotionFlowSubtitle } from './PromotionFlowSubtitle';
import * as S from '@/components/Ad/Promotions/PromotionFlow.styles';
import { calculatePercentageOff, formatDate } from '@/utils/helpers';
import { AdBundle } from '@/api/types/categories.types';
import { AdService, ServiceId } from '@/api/types/services.types';
import { AdKind } from '@/api/types/common.types';
import {
  getServiceLabel,
  mapPaymentDataToUrl,
  mapQueryStringToPaymentData,
} from '@/views/Wallet/Finish.helpers';
import { buildWebViewPaymentData } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.helpers';
import { PaymentMethod } from '@/api/types/payments.types';
import { PAID_UNTIL_TYPES } from '@/components/Ad/Promotions/PromotionFlow.constants';

export function getPriceWithDiscount(
  price: number,
  isWallet: boolean,
  discount: number
) {
  return isWallet && discount > 0
    ? calculatePercentageOff(price, discount)
    : price;
}

export function buildServiceIdForService(
  service: AdService,
  type: AdKind | null
) {
  if (!type) return '';
  return `${ServiceId[type]}${service.period}`;
}

export function buildServiceIdForBundle(bundle: AdBundle | null) {
  return `bundle_${bundle?.id}`;
}

export function buildServiceId(
  service: AdService | null,
  type: AdKind | null,
  bundle: AdBundle | null,
  isBundles: boolean
) {
  return isBundles
    ? buildServiceIdForBundle(bundle)
    : service && buildServiceIdForService(service, type);
}

function getMappedUrlForBundle({
  data,
  adDetails,
  categoryDetails,
  bundle,
  walletDiscount,
  isWallet,
  payment,
}: GetMappedUrlForBundleArgs) {
  if (
    adDetails &&
    data.createPayment.entity.paymentLegacyId &&
    data.createPayment.entity.paymentDate &&
    categoryDetails &&
    bundle
  ) {
    return mapPaymentDataToUrl({
      id: data.createPayment.entity.paymentLegacyId,
      price: getPriceWithDiscount(
        bundle.price,
        isWallet,
        walletDiscount
      ).toString(),
      date: data.createPayment.entity.paymentDate,
      adId: adDetails.legacyResourceId.toString(),
      categoryName: categoryDetails.name,
      adsCount: bundle.count.toString(),
      paymentMethod: payment,
    });
  }

  return '';
}

function getMappedUrlForService({
  data,
  adDetails,
  service,
  type,
  walletDiscount,
  isWallet,
  payment,
}: GetMappedUrlForServiceArgs) {
  if (
    adDetails &&
    data.createPayment.entity.paymentLegacyId &&
    data.createPayment.entity.paymentDate &&
    service &&
    type
  ) {
    return mapPaymentDataToUrl({
      id: data.createPayment.entity.paymentLegacyId,
      price: getPriceWithDiscount(
        getLessPrice(service),
        isWallet,
        walletDiscount
      ).toString(),
      date: data.createPayment.entity.paymentDate,
      adId: adDetails.legacyResourceId.toString(),
      serviceType: type,
      period: service.period.toString(),
      paymentMethod: payment,
    });
  }
  return '';
}

export function getUrl({
  isBundles,
  bundle,
  service,
  type,
  data,
  adDetails,
  categoryDetails,
  walletDiscount,
  isWallet,
  payment,
}: GetUrlArgs) {
  if (isBundles)
    return getMappedUrlForBundle({
      data,
      adDetails,
      categoryDetails,
      bundle,
      walletDiscount,
      isWallet,
      payment,
    });
  if (service && type)
    return getMappedUrlForService({
      data,
      adDetails,
      service,
      type,
      walletDiscount,
      isWallet,
      payment,
    });
  return '';
}

function getSubtitle({
  t,
  type,
  isSuccessPopup,
  vippedUntil,
  featuredUntil,
}: GetSubtitleArgs) {
  switch (type) {
    case AdKind.Bumped: {
      return isSuccessPopup ? (
        t(`pages.ad.${isSuccessPopup ? 'ad_bumped' : 'up_ad_subtitle'}`)
      ) : (
        <S.Subtitle>
          {t(`pages.ad.${isSuccessPopup ? 'ad_bumped' : 'up_ad_subtitle'}`)}
        </S.Subtitle>
      );
    }
    case AdKind.Vipped: {
      return isSuccessPopup ? (
        t('pages.ad.ad_vipped', { dateTime: vippedUntil })
      ) : (
        <PromotionFlowSubtitle type="vip" />
      );
    }
    case AdKind.Premium: {
      return isSuccessPopup ? (
        t('pages.ad.ad_featured', { dateTime: featuredUntil })
      ) : (
        <PromotionFlowSubtitle type="premium" />
      );
    }
    default: {
      return <></>;
    }
  }
}

export const getPromotionsData = ({
  t,
  isSuccessPopup = false,
  vippedUntil,
  featuredUntil,
}: GetPromotionsDataArgs): Record<AdKind, GetPromotionsDataType> => {
  const subtitle = (type: AdKind) =>
    getSubtitle({
      t,
      type,
      isSuccessPopup,
      vippedUntil,
      featuredUntil,
    });
  return {
    [AdKind.Bumped]: {
      iconName: 'upload-green',
      title: t('pages.ad.up_ad'),
      subtitle: subtitle(AdKind.Bumped),
    },
    [AdKind.Vipped]: {
      iconName: 'vip',
      title: t('pages.ad.make_vip'),
      subtitle: subtitle(AdKind.Vipped),
    },
    [AdKind.Premium]: {
      iconName: 'premium',
      title: t('pages.ad.make_premium'),
      subtitle: subtitle(AdKind.Premium),
    },
  };
};

export const getPayButtonDataStatAttrValue = (
  payment: PaymentMethod,
  type: AdKind | null
) => {
  if (payment === PaymentMethod.My_card) {
    const myCardPrefix = {
      [AdKind.Bumped]: 'bump',
      [AdKind.Vipped]: 'vip',
      [AdKind.Premium]: 'feature',
    };
    return `${type ? myCardPrefix[type] : 'bundle'}-pay-with-token`;
  }
};

export const buildDataAndOpenWebViewPaymentModal = ({
  i18n,
  url,
  data,
  service,
  type,
  bundle,
  isBundles,
  isWallet,
  openWebViewPaymentModal,
}: BuildDataAndOpenWebViewPaymentModalArgs) => {
  const query = querystring.parse(url.split('?')[1]);
  const paymentData = mapQueryStringToPaymentData(query);
  const { targetLegacyId, paymentDate, paymentLegacyId } =
    data.createPayment.entity;
  const serviceId = buildServiceId(service, type, bundle, isBundles);

  if (
    paymentData &&
    targetLegacyId &&
    paymentDate &&
    paymentLegacyId &&
    serviceId
  ) {
    const paymentModalData = buildWebViewPaymentData(i18n.t, {
      paymentDate,
      paymentId: paymentLegacyId,
      targetId: targetLegacyId,
      amount: paymentData.price,
      serviceName: getServiceLabel(i18n, paymentData),
      serviceId,
      isWallet,
    });
    openWebViewPaymentModal(paymentModalData);
  }
};

export const getPaidUntilDate = ({
  adDetails,
  type,
  t,
}: GetPaidUntilDateArgs) => {
  if (!type || type === AdKind.Bumped) return null;

  const untilDate = type && adDetails?.[PAID_UNTIL_TYPES[type]];

  return untilDate
    ? t('pages.ad.paid_until', {
        date: formatDate(String(untilDate), 'dd MMMM yyyy, HH:mm'),
      })
    : null;
};
